/**
 * IterationDetailModal - Detailed drill-down view for specific Monte Carlo iterations
 *
 * This component displays complete year-by-year simulation data for a specific Monte Carlo
 * iteration, similar to the Einzelsimulation tables but within a modal dialog.
 *
 * Features:
 * - Year-by-year breakdown for both 3-Topf and 1-Topf strategies
 * - Complete pot balances, transactions, and market conditions
 * - Tax calculations and withdrawal details
 * - Crisis indicators and capital index tracking
 * - Export functionality for detailed analysis
 *
 * CRITICAL: Data Consistency
 * - ALWAYS uses stored jahresergebnisse from the original Monte Carlo run
 * - NEVER re-simulates iterations (would produce non-deterministic results with GARCH)
 * - Ensures 100% consistency with table display and export data
 * - Throws error if stored results are not available
 *
 * @author Portfolio-3Topf Team
 * @version 2.4.10 - Fixed critical data consistency bug
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Modal,
  SpaceBetween,
  Spinner,
  Table,
  Tabs,
} from '@cloudscape-design/components';
import { ThreeTopfYearData } from '@/types/simulation';
import { OneTopfYearData } from '@/types';
import { CombinedMonteCarloEngine, CombinedMonteCarloResult } from '@/services/CombinedMonteCarloEngine';
import { getEndOfYearMarketStatus, getStartOfYearMarketStatus } from '@/utils/MarketStatusUtils';

interface IterationDetailModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  iteration: number;
  result?: CombinedMonteCarloResult; // CRITICAL FIX v2.4.12: Pass result directly for 100% consistency
  monteCarloEngine: CombinedMonteCarloEngine;
  scenarioType: 'worst-case-3topf' | 'worst-case-1topf' | 'general';
  // scenarioDescription is no longer used - description is ALWAYS generated from jahresergebnisse
  scenarioDescription?: string; // Keep for backwards compatibility but ignored
  crisisDefinition: 'negative-return' | 'kapitalindex-drawdown'; // Crisis definition for market status display
}

interface DetailedSimulationData {
  threeTopf: ThreeTopfYearData[];
  oneTopf: OneTopfYearData[];
  sharedRates?: any;
  warning?: string;
}

export const IterationDetailModal: React.FC<IterationDetailModalProps> = ({
  isVisible,
  onDismiss,
  iteration,
  result,
  monteCarloEngine,
  scenarioType,
  crisisDefinition,
  // scenarioDescription is intentionally not destructured - we don't use it
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [simulationData, setSimulationData] = useState<DetailedSimulationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [correctedDescription, setCorrectedDescription] = useState<string | null>(null);

  // Load detailed simulation data when modal opens or iteration changes
  useEffect(() => {
    if (isVisible) {
      // CRITICAL FIX v2.4.12: Always reload when iteration changes
      // Clear old data first to prevent stale data display
      setSimulationData(null);
      setCorrectedDescription(null);
      loadIterationDetails();
    }
  }, [isVisible, iteration]);

  const loadIterationDetails = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // CRITICAL FIX v2.4.12: ALWAYS use the result passed directly from the parent component
      // This is the SINGLE SOURCE OF TRUTH and ensures 100% consistency with table/export data
      // This eliminates any possibility of engine instance mismatch or lost stored results
      
      let dataSource: CombinedMonteCarloResult | undefined;
      
      // Priority 1: Use the result passed directly from parent (HIGHEST PRIORITY)
      if (result && result.threeTopf.jahresergebnisse && result.oneTopf.jahresergebnisse) {
        dataSource = result;
      } 
      // Priority 2: Fallback to engine stored results (for backwards compatibility)
      else {
        dataSource = monteCarloEngine.getStoredIterationResult(iteration);
        
        if (!dataSource) {
          throw new Error(
            `Stored results not available for iteration ${iteration}. ` +
            `This iteration may not exist in the Monte Carlo run. ` +
            `Please re-run the Monte Carlo simulation.`
          );
        }
      }
      
      // Validate data integrity
      if (!dataSource.threeTopf.jahresergebnisse || dataSource.threeTopf.jahresergebnisse.length === 0) {
        throw new Error(
          `3-Topf jahresergebnisse not available for iteration ${iteration}. ` +
          `The stored data may be incomplete. Please re-run the Monte Carlo simulation.`
        );
      }
      
      if (!dataSource.oneTopf.jahresergebnisse || dataSource.oneTopf.jahresergebnisse.length === 0) {
        throw new Error(
          `1-Topf jahresergebnisse not available for iteration ${iteration}. ` +
          `The stored data may be incomplete. Please re-run the Monte Carlo simulation.`
        );
      }
      
      // Use the authoritative data source
      const data: DetailedSimulationData = {
        threeTopf: dataSource.threeTopf.jahresergebnisse,
        oneTopf: dataSource.oneTopf.jahresergebnisse,
      };
      
      setSimulationData(data);
      
      // ALWAYS generate description from ACTUAL simulation data (jahresergebnisse)
      // This is the SINGLE SOURCE OF TRUTH - no external description can override this
      if (data.threeTopf.length > 0 && data.oneTopf.length > 0) {
        const threeTopfYears = data.threeTopf.length;
        const oneTopfYears = data.oneTopf.length;
        
        // Use the actual 'alter' field from the LAST year in jahresergebnisse
        // This is the correct depletion age as recorded during simulation
        const threeTopfDepletionAge = data.threeTopf[data.threeTopf.length - 1].alter;
        const oneTopfDepletionAge = data.oneTopf[data.oneTopf.length - 1].alter;
        
        // Generate description based on scenario type
        let description: string;
        if (scenarioType === 'worst-case-3topf') {
          const advantage = oneTopfYears - threeTopfYears;
          description = `3-Topf Worst-Case: Depletion bei ${threeTopfDepletionAge}J, 1-Topf Vorteil +${advantage}J`;
        } else if (scenarioType === 'worst-case-1topf') {
          const advantage = threeTopfYears - oneTopfYears;
          description = `1-Topf Worst-Case: Depletion bei ${oneTopfDepletionAge}J, 3-Topf Vorteil +${advantage}J`;
        } else {
          // For general failures, show both depletion ages
          description = `3-Topf Depletion bei ${threeTopfDepletionAge}J (${threeTopfYears} Jahre), 1-Topf Depletion bei ${oneTopfDepletionAge}J (${oneTopfYears} Jahre)`;
        }
        
        setCorrectedDescription(description);
      }
    } catch (err) {
      console.error('Failed to load iteration details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load iteration details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = () => {
    if (!simulationData) {return;}

    const exportData = {
      iteration,
      scenarioType,
      scenarioDescription: correctedDescription || `Iteration #${iteration}`,
      threeTopfData: simulationData.threeTopf,
      oneTopfData: simulationData.oneTopf,
      exportTimestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `monte-carlo-iteration-${iteration}-details.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getScenarioTypeLabel = () => {
    switch (scenarioType) {
      case 'worst-case-3topf':
        return 'Worst-Case für 3-Topf-Strategie';
      case 'worst-case-1topf':
        return 'Worst-Case für 1-Topf-Strategie';
      default:
        return 'Monte Carlo Iteration';
    }
  };

  const getScenarioTypeColor = () => {
    switch (scenarioType) {
      case 'worst-case-3topf':
        return 'red';
      case 'worst-case-1topf':
        return 'severity-medium';
      default:
        return 'blue';
    }
  };

  // Define table columns for 3-Topf strategy
  const threeTopfColumns = [
    {
      id: 'alter',
      header: 'Alter',
      cell: (item: ThreeTopfYearData) => item.alter,
      width: 60,
    },
    {
      id: 'entnahme',
      header: 'Entnahme',
      cell: (item: ThreeTopfYearData) => 
        `${Math.round(item.gesamtentnahme).toLocaleString('de-DE')} €`,
      width: 100,
    },
    {
      id: 'inflation',
      header: 'Inflation',
      cell: (item: ThreeTopfYearData) => `${item.inflationsrate.toFixed(1)}%`,
      width: 80,
    },
    {
      id: 'verbrauchstopf',
      header: 'Verbrauchstopf',
      cell: (item: ThreeTopfYearData) => (
        <Box textAlign="right" fontSize="body-s">
          <div>{Math.round(item.verbrauchstopfSaldo || item.verbrauchstopf || 0).toLocaleString('de-DE')} €</div>
          <div style={{ color: item.verbrauchstopfZinssatz >= 0 ? '#0972d3' : '#d13212' }}>
            {item.verbrauchstopfZinssatz.toFixed(1)}%
          </div>
        </Box>
      ),
      width: 120,
    },
    {
      id: 'zinstopf',
      header: 'Zinstopf',
      cell: (item: ThreeTopfYearData) => (
        <Box textAlign="right" fontSize="body-s">
          <div>{Math.round(item.zinstopfSaldo || item.zinstopf || 0).toLocaleString('de-DE')} €</div>
          <div style={{ color: item.zinstopfZinssatz >= 0 ? '#0972d3' : '#d13212' }}>
            {item.zinstopfZinssatz.toFixed(1)}%
          </div>
        </Box>
      ),
      width: 120,
    },
    {
      id: 'investitionstopf',
      header: 'Investitionstopf',
      cell: (item: ThreeTopfYearData) => (
        <Box textAlign="right" fontSize="body-s">
          <div>{Math.round(item.investitionstopfSaldo || item.investitionstopf || 0).toLocaleString('de-DE')} €</div>
          <div style={{ color: item.investitionstopfRendite >= 0 ? '#0972d3' : '#d13212' }}>
            {item.investitionstopfRendite.toFixed(1)}%
          </div>
        </Box>
      ),
      width: 140,
    },
    {
      id: 'kapitalIndex',
      header: 'Kapitalindex',
      cell: (item: ThreeTopfYearData) => {
        // Use the same logic as EinzelSimulationTab for consistency
        const currentIndex = simulationData?.threeTopf.findIndex(d => d.jahr === item.jahr) || 0;
        
        let startIndex = 100;
        if (currentIndex > 0) {
          const prevYear = simulationData?.threeTopf[currentIndex - 1];
          startIndex = prevYear?.kapitalIndex || 100;
        }

        const endIndex = item.kapitalIndex || 100;

        return (
          <Box textAlign="right" fontSize="body-s">
            <div>01.01: {Math.round(startIndex).toLocaleString('de-DE')}</div>
            <div>31.12: {Math.round(endIndex).toLocaleString('de-DE')}</div>
          </Box>
        );
      },
      width: 120,
    },
    {
      id: 'marktSituation',
      header: 'Marktlage (Jahresende)',
      cell: (item: ThreeTopfYearData) => {
        // Find current year index and previous year data
        const currentIndex = simulationData?.threeTopf.findIndex(d => d.jahr === item.jahr) || 0;
        const previousYear = currentIndex > 0 ? simulationData?.threeTopf[currentIndex - 1] : undefined;
        
        // Use crisis definition from props (passed from parent component)
        // This ensures consistency with the Monte Carlo simulation settings
        
        // Get start-of-year and end-of-year market status
        const startOfYearStatus = getStartOfYearMarketStatus(
          item,
          previousYear,
          simulationData?.threeTopf || [],
          crisisDefinition
        );
        const endOfYearStatus = getEndOfYearMarketStatus(item);
        
        return (
          <Box textAlign="center" fontSize="body-s">
            <div style={{ marginBottom: '2px' }}>
              01.01: <Badge color={startOfYearStatus === 'Krise' ? 'red' : 'green'} >
                {startOfYearStatus}
              </Badge>
            </div>
            <div>
              31.12: <Badge color={endOfYearStatus === 'Krise' ? 'red' : 'green'}>
                {endOfYearStatus}
              </Badge>
            </div>
          </Box>
        );
      },
      width: 110,
    },
    {
      id: 'auffuellAktion',
      header: 'Info',
      cell: (item: ThreeTopfYearData) => (
        <div style={{ 
          wordWrap: 'break-word', 
          lineHeight: '1.2', 
          fontSize: '0.875rem',
          textAlign: 'left',
          whiteSpace: 'pre-line'
        }}>
          {item.auffuellAktion || '-'}
        </div>
      ),
      minWidth: 400,
    },
  ];

  // Define table columns for 1-Topf strategy  
  const oneTopfColumns = [
    {
      id: 'alter',
      header: 'Alter',
      cell: (item: OneTopfYearData) => item.alter,
      width: 60,
    },
    {
      id: 'entnahme',
      header: 'Entnahme',
      cell: (item: OneTopfYearData) => 
        `${Math.round(item.gesamtentnahme).toLocaleString('de-DE')} €`,
      width: 100,
    },
    {
      id: 'inflation',
      header: 'Inflation',
      cell: (item: OneTopfYearData) => `${item.inflationsrate.toFixed(1)}%`,
      width: 80,
    },
    {
      id: 'investitionstopf',
      header: 'Investitionstopf',
      cell: (item: OneTopfYearData) => (
        <Box textAlign="right" fontSize="body-s">
          <div>{Math.round(item.investitionstopfSaldo || 0).toLocaleString('de-DE')} €</div>
          <div style={{ color: item.investitionstopfRendite >= 0 ? '#0972d3' : '#d13212' }}>
            {item.investitionstopfRendite.toFixed(1)}%
          </div>
        </Box>
      ),
      width: 140,
    },
    {
      id: 'kapitalIndex',
      header: 'Kapitalindex',
      cell: (item: OneTopfYearData) => {
        const currentIndex = simulationData?.oneTopf.findIndex(d => d.jahr === item.jahr) || 0;
        
        let startIndex = 100;
        if (currentIndex > 0) {
          const prevYear = simulationData?.oneTopf[currentIndex - 1];
          startIndex = prevYear?.kapitalIndex || 100;
        }

        const endIndex = item.kapitalIndex || 100;

        return (
          <Box textAlign="right" fontSize="body-s">
            <div>01.01: {Math.round(startIndex).toLocaleString('de-DE')}</div>
            <div>31.12: {Math.round(endIndex).toLocaleString('de-DE')}</div>
          </Box>
        );
      },
      width: 120,
    },
    {
      id: 'marktSituation',
      header: 'Marktlage',
      cell: (item: OneTopfYearData) => {
        // Find current year index and previous year data
        const currentIndex = simulationData?.oneTopf.findIndex(d => d.jahr === item.jahr) || 0;
        const previousYear = currentIndex > 0 ? simulationData?.oneTopf[currentIndex - 1] : undefined;
        
        // Use crisis definition from props (passed from parent component)
        // This ensures consistency with the Monte Carlo simulation settings
        
        // Get start-of-year and end-of-year market status
        const startOfYearStatus = getStartOfYearMarketStatus(
          item,
          previousYear,
          simulationData?.oneTopf || [],
          crisisDefinition
        );
        const endOfYearStatus = getEndOfYearMarketStatus(item);
        
        return (
          <Box textAlign="center" fontSize="body-s">
            <div style={{ marginBottom: '2px' }}>
              01.01: <Badge color={startOfYearStatus === 'Krise' ? 'red' : 'green'} >
                {startOfYearStatus}
              </Badge>
            </div>
            <div>
              31.12: <Badge color={endOfYearStatus === 'Krise' ? 'red' : 'green'}>
                {endOfYearStatus}
              </Badge>
            </div>
          </Box>
        );
      },
      width: 110,
    },
    {
      id: 'entnahmeAktion',
      header: 'Info',
      cell: (item: OneTopfYearData) => (
        <div style={{ wordWrap: 'break-word', lineHeight: '1.2', fontSize: '0.875rem' }}>
          {item.entnahmeAktion || '-'}
        </div>
      ),
      width: 200,
    },
  ];

  return (
    <Modal
      onDismiss={onDismiss}
      visible={isVisible}
      size="max"
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {simulationData && (
                <Button variant="normal" onClick={handleExportData}>
                  JSON Export
                </Button>
              )}
              <Button variant="primary" onClick={onDismiss}>
                Schließen
              </Button>
            </SpaceBetween>
          }
        >
          {getScenarioTypeLabel()} - Iteration #{iteration}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Scenario Info */}
        <Container>
          <ColumnLayout columns={3}>
            <div>
              <Box variant="awsui-key-label">Szenario-Typ</Box>
              <Badge color={getScenarioTypeColor()}>{getScenarioTypeLabel()}</Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Iteration</Box>
              <Box variant="awsui-value-large">#{iteration}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Beschreibung</Box>
              <Box fontSize="body-s">{correctedDescription || 'Lade Daten...'}</Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* Summary Information */}
        {simulationData && !isLoading && (
          <Container>
            <Header variant="h2">Zusammenfassung der Iteration</Header>
            
            {/* Warning if re-simulation produced different results */}
            {simulationData.warning && (
              <Alert type="warning" header="Inkonsistenz bei Re-Simulation">
                {simulationData.warning}
              </Alert>
            )}
            
            <ColumnLayout columns={2}>
              <div>
                <Box variant="h3">3-Topf-Strategie</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <div>
                    <Box variant="awsui-key-label">Simulierte Jahre</Box>
                    <Box>{simulationData.threeTopf.length}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Endkapital</Box>
                    <Box>
                      {simulationData.threeTopf.length > 0 
                        ? `${Math.round(simulationData.threeTopf[simulationData.threeTopf.length - 1]?.gesamtsaldo || 0).toLocaleString('de-DE')} €`
                        : 'N/A'}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Gesamtentnahmen</Box>
                    <Box>
                      {simulationData.threeTopf
                        .reduce((sum, year) => sum + (year.gesamtentnahme || 0), 0)
                        .toLocaleString('de-DE')} €
                    </Box>
                  </div>
                </SpaceBetween>
              </div>
              <div>
                <Box variant="h3">1-Topf-Strategie</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <div>
                    <Box variant="awsui-key-label">Simulierte Jahre</Box>
                    <Box>{simulationData.oneTopf.length}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Endkapital</Box>
                    <Box>
                      {simulationData.oneTopf.length > 0 
                        ? `${Math.round(simulationData.oneTopf[simulationData.oneTopf.length - 1]?.gesamtsaldo || 0).toLocaleString('de-DE')} €`
                        : 'N/A'}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Gesamtentnahmen</Box>
                    <Box>
                      {simulationData.oneTopf
                        .reduce((sum, year) => sum + (year.gesamtentnahme || 0), 0)
                        .toLocaleString('de-DE')} €
                    </Box>
                  </div>
                </SpaceBetween>
              </div>
            </ColumnLayout>
          </Container>
        )}

        {/* Loading State */}
        {isLoading && (
          <Container>
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
              <Box variant="p" color="text-status-info" margin={{ top: 'm' }}>
                Lade detaillierte Simulationsdaten für Iteration #{iteration}...
              </Box>
            </Box>
          </Container>
        )}

        {/* Error State */}
        {error && (
          <Alert type="error" header="Fehler beim Laden der Daten">
            {error}
          </Alert>
        )}

        {/* Detailed Data Tables */}
        {simulationData && !isLoading && (
          <Tabs
            tabs={[
              {
                label: '3-Topf-Strategie',
                id: 'three-topf',
                content: (
                  <Container>
                    <Table
                      columnDefinitions={threeTopfColumns}
                      items={simulationData.threeTopf}
                      loadingText="Lade 3-Topf-Daten..."
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>Keine 3-Topf-Daten verfügbar</b>
                          <Box variant="p" color="inherit">
                            Die Simulation konnte nicht durchgeführt werden.
                          </Box>
                        </Box>
                      }
                      header={
                        <Header
                          counter={`(${simulationData.threeTopf.length} Jahre)`}
                          description="Vollständige Jahresanalyse der 3-Topf-Strategie für diese Monte Carlo Iteration"
                        >
                          3-Topf-Strategie - Jahresdetails
                        </Header>
                      }
                    />
                  </Container>
                ),
              },
              {
                label: '1-Topf-Strategie',
                id: 'one-topf',
                content: (
                  <Container>
                    <Table
                      columnDefinitions={oneTopfColumns}
                      items={simulationData.oneTopf}
                      loadingText="Lade 1-Topf-Daten..."
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>Keine 1-Topf-Daten verfügbar</b>
                          <Box variant="p" color="inherit">
                            Die Simulation konnte nicht durchgeführt werden.
                          </Box>
                        </Box>
                      }
                      header={
                        <Header
                          counter={`(${simulationData.oneTopf.length} Jahre)`}
                          description="Vollständige Jahresanalyse der 1-Topf-Strategie für diese Monte Carlo Iteration"
                        >
                          1-Topf-Strategie - Jahresdetails
                        </Header>
                      }
                    />
                  </Container>
                ),
              },
            ]}
          />
        )}
      </SpaceBetween>
    </Modal>
  );
};

